import Container from '../../components/container/Container';
import { exitApp } from '../../hooks/utils';
import React, { useState } from 'react';
import { KbaAnswersSubmit, KbaQuestion } from './interfaces';
import RadioGroup from './RadioGroup';
import Loader from '../../components/loader/Loader';
import { useGetKbaQuestions, useSettings } from '../../hooks/queryHooks';
import { useSendKbaAnswersMutation } from '../../hooks/mutationHooks';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import Spinner from '../../components/spinner/Spinner';

const KbaPage = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const { settings } = useSettings();
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, string>>({});
  const [errorMessage, setErrorMessage] = useState<string>('');

  const { questions, isFetching, refetch } = useGetKbaQuestions();

  const handleAnswerChange = (questionId: string, answerId: string) => {
    setSelectedAnswers(prev => ({
      ...prev,
      [questionId]: answerId
    }));
    if (errorMessage) setErrorMessage('');
  };

  const sendKbaAnswersMutation = useSendKbaAnswersMutation();

  React.useEffect(() => {
    if (sendKbaAnswersMutation.isSuccess && sendKbaAnswersMutation.data?.status) {
      switch (sendKbaAnswersMutation.data.status) {
        case 'Verified':
          handleVerified();
          break;
        case 'Failed':
          handleFailed();
          break;
        case 'Retry':
          handleRetry();
          break;
        case 'RequiresAdditionalQuestions':
          handleRequiresAdditionalQuestions();
          break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendKbaAnswersMutation.isSuccess, sendKbaAnswersMutation.data]);

  const handleVerified = () => {
    history.push('/tu-end-success');
  };

  const handleFailed = () => {
    history.push('/bank-select', {
      hasFailedVerification: true
    });
  };

  const handleRetry = () => {
    setErrorMessage('We have been unable to verify. Please try again.');
    setSelectedAnswers({});
  };

  const handleRequiresAdditionalQuestions = () => {
    setErrorMessage('We require additional information');
    refetch();
    setSelectedAnswers({});
  };

  const submitAnswers = () => {
    const answersToSubmit: KbaAnswersSubmit[] = Object.entries(selectedAnswers).map(([questionId, answerId]) => ({
      questionId,
      answerId
    }));

    sendKbaAnswersMutation.mutate(answersToSubmit);
  };

  const allQuestionsAnswered =
    !isFetching && questions?.every((question: { id: string | number }) => selectedAnswers[question.id] !== undefined);

  if (isFetching) return <Loader display />;

  if (!isFetching && questions?.length === 0) {
    return (
      <Container>
        <h1 className="uppercase text-md sm:text-lg mb-2 font-semi-bold text-primary-2">Something went wrong</h1>
      </Container>
    );
  }

  if (sendKbaAnswersMutation.isPending) {
    return <Spinner text="Verifying.." />;
  }

  return (
    <Container
      primaryBtn={{
        text: t('Continue'),
        onClick: submitAnswers,
        disabled: !allQuestionsAnswered || sendKbaAnswersMutation.isPending
      }}
      {...(settings?.redirectUrl && {
        secondaryBtn: {
          text: t('Abort & return to partner'),
          onClick: () => exitApp()
        }
      })}
    >
      <h1 className="uppercase text-md sm:text-lg mb-2 font-semi-bold text-primary-2">Verify your identity</h1>
      <p className="text-base text-primary-2">
        To help verify your identity, can you please answer the questions below.
      </p>

      {errorMessage && <p className="text-negative pt-4">{errorMessage}</p>}

      <div>
        {questions?.map((question: KbaQuestion) => (
          <RadioGroup
            key={question.id}
            question={question}
            selectedAnswerId={selectedAnswers[question.id]}
            onAnswerChange={handleAnswerChange}
          />
        ))}
      </div>
    </Container>
  );
};

export default KbaPage;
